import { NOT_LOGGED_IN_USER_ID } from '@ori/auth';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  AUTH_CODE_STORAGE_PARAMETER_FOR_GENESYS,
  AUTH_IFRAME_ID,
  ISSUER_STORAGE_PARAMETER_FOR_GENESYS,
  REDIRECT_URI,
} from '../constants';
import { generateVerifierAndChallenge } from '../utils/authUtils';
import { useConfiguration } from './configurationContext';

export interface ExternalAuthContext {
  codeVerifier: string;
}

interface MessageEvent {
  data: {
    source: string;
    code: string;
    iss: string;
  };
}

export const externalAuthContext = createContext<ExternalAuthContext>({
  codeVerifier: '',
});

const { Provider } = externalAuthContext;

export const ExternalAuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const { identityUrl, tenant, customerId } = useConfiguration();

  // Quick hack for identityUrl until it's fixed on FP, sometimes it contains url {tenant}.online.ori which is wrong.
  const fixedIdentityUrl = identityUrl.includes('online.ori') ? 'https://identity-we.oriflame.com' : identityUrl;

  const [codeVerifier, setCodeVerifier] = useState('');

  const generateAuthUrl = useCallback(
    (codeChallenge: string) => {
      const url = new URL(fixedIdentityUrl);
      url.pathname = '/connect/authorize';
      url.searchParams.append('scope', 'all');
      url.searchParams.append('prompt', 'none');
      url.searchParams.append('client_id', 'GenesysWebMessenger');
      url.searchParams.append('response_type', 'code');
      url.searchParams.append('code_challenge_method', `S256`);
      url.searchParams.append('acr_values', `tenant:${tenant}`);
      url.searchParams.append('code_challenge', codeChallenge);
      url.searchParams.append('redirect_uri', REDIRECT_URI);

      return url.toString();
    },
    [fixedIdentityUrl, tenant],
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.source === 'genesysAuth' && event.data.code && event.data.iss) {
        localStorage.setItem(AUTH_CODE_STORAGE_PARAMETER_FOR_GENESYS, event.data.code);
        localStorage.setItem(ISSUER_STORAGE_PARAMETER_FOR_GENESYS, event.data.iss);

        document.getElementById(AUTH_IFRAME_ID)?.remove();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (customerId !== NOT_LOGGED_IN_USER_ID) {
      generateVerifierAndChallenge()
        .then((r) => {
          const authUrl = generateAuthUrl(r.codeChallenge);

          setCodeVerifier(r.codeVerifier);

          const iframe = document.createElement('iframe');
          iframe.id = AUTH_IFRAME_ID;
          iframe.src = authUrl;
          iframe.sandbox.add('allow-forms');
          iframe.sandbox.add('allow-scripts');
          iframe.sandbox.add('allow-same-origin');
          iframe.hidden = true;
          iframe.style.visibility = 'hidden';
          iframe.style.display = 'none';

          document.body.append(iframe);

          return () => iframe.remove();
        })
        .catch((error) => {
          // eslint-disable-next-line no-console -- error
          console.log(error);
        });
    }
  }, [customerId, generateAuthUrl]);

  const value = useMemo(
    () => ({
      codeVerifier,
    }),
    [codeVerifier],
  );

  return <Provider value={value}>{children}</Provider>;
};

export const useExternalAuth = () => useContext(externalAuthContext);
